/**
 * API Configuration
 * Backend base URL configuration.
 */


// Base URL for the PHP backend
// NOTE: Backend is in /backend/ folder, so API path is /backend/api
// You can override this at build time with REACT_APP_API_BASE_URL.
const MAIN_API_URL = 'https://360coordinates.com/backend/api';
// const MAIN_API_URL = 'https://testapi.360coordinates.com/backend/api';
const DEMO1_API_URL = 'https://demo1.360coordinates.com/backend/api';
const TESTAPI_API_URL = 'https://testapi.360coordinates.com/backend/api';
const LOCAL_API_URL = 'http://localhost/Fullstack/backend/api';

function inferApiBaseUrl() {
  // Highest priority: explicit build-time override
  if (process.env.REACT_APP_API_BASE_URL) return process.env.REACT_APP_API_BASE_URL;

  // Runtime inference based on current hostname
  if (typeof window !== 'undefined' && window.location && window.location.hostname) {
    const host = window.location.hostname.toLowerCase();
    // if (host === 'localhost' || host === '127.0.0.1') return LOCAL_API_URL;
    if (host.startsWith('testapi.')) return TESTAPI_API_URL;
    if (host.startsWith('demo1.')) return DEMO1_API_URL;
    return MAIN_API_URL;
  }

  // Fallback (SSR / unknown): default to main domain
  return MAIN_API_URL;
}

const API_URL = inferApiBaseUrl();

export const API_BASE_URL = API_URL;

/**
 * Infinitio SMS (Create React App: set REACT_APP_INFINITIO_SMS_* in `.env`).
 * React Native projects typically map the same names via `@env` / babel-plugin-dotenv.
 */
const INFINITIO_SMS_API_KEY =
  typeof process.env.REACT_APP_INFINITIO_SMS_API_KEY === 'string'
    ? process.env.REACT_APP_INFINITIO_SMS_API_KEY.trim()
    : '';

/** DLT template id; env overrides. Empty env = omit templateid in SMS request (non-template API). */
const INFINITIO_SMS_TEMPLATE_ID = (() => {
  if (typeof process.env.REACT_APP_INFINITIO_SMS_TEMPLATE_ID !== 'string') {
    return '1707177856711766644';
  }
  return process.env.REACT_APP_INFINITIO_SMS_TEMPLATE_ID.trim();
})();

export const API_CONFIG = {
  API_BASE_URL,
  INFINITIO_SMS_SEND_URL: 'https://sms.infinitiodigital.com/api/bulkmt/SendSMS',
  INFINITIO_SMS_API_KEY,
  INFINITIO_SMS_SENDER_ID: 'VEINFO',
  INFINITIO_SMS_CHANNEL: 'Trans',
  INFINITIO_SMS_DCS: '0',
  INFINITIO_SMS_FLASHSMS: '0',
  INFINITIO_SMS_ROUTE: '00',
  INFINITIO_SMS_TEMPLATE_ID,
  /**
   * Trailing literal required by your Infinitio DLT template (sample: `... for test VEDANTINFO`).
   * Portal `Rejected(666)` with matching text usually means the body did not match the registered template.
   * Set to '' only if your approved template has no fixed suffix.
   */
  INFINITIO_SMS_DLT_MESSAGE_SUFFIX: 'VEDANTINFO',
};

// API Endpoints
export const API_ENDPOINTS = {
  // Auth
  LOGIN: '/auth/login.php',
  LOGIN_PHONE: '/auth/login-phone.php',
  LOGIN_PHONE_WIDGET: '/auth/login-phone-widget.php',
  REGISTER: '/auth/register.php',
  VERIFY_TOKEN: '/auth/verify.php',
  SWITCH_ROLE: '/auth/switch-role.php',
  DELETE_ACCOUNT: '/auth/delete-account.php',
  FORGOT_PASSWORD_INIT: '/auth/forgot-password-init.php',
  RESET_PASSWORD: '/auth/reset-password.php',
  
  // Seller Properties
  SELLER_PROPERTIES: '/seller/properties/list.php',
  SELLER_ADD_PROPERTY: '/seller/properties/add.php',
  SELLER_UPDATE_PROPERTY: '/seller/properties/update.php',
  SELLER_DELETE_PROPERTY: '/seller/properties/delete.php',
  SELLER_RENEW_PROPERTY: '/seller/properties/renew.php',
  SELLER_TOGGLE_PROPERTY_VISIBILITY: '/seller/properties/toggle-visibility.php',
  
  // Seller Dashboard
  SELLER_STATS: '/seller/dashboard/stats.php',
  SELLER_SUBSCRIPTIONS: '/seller/subscriptions.php',
  
  // Seller Inquiries
  SELLER_INQUIRIES: '/seller/inquiries/list.php',
  SELLER_UPDATE_INQUIRY: '/seller/inquiries/updateStatus.php',
  
  // Seller Buyers (for chat)
  SELLER_GET_BUYER: '/seller/buyers/get.php',

  // Seller Leads (view_owner interactions)
  SELLER_LEADS: '/seller/leads/list.php',
  
  // Seller Profile
  SELLER_PROFILE: '/seller/profile/get.php',
  SELLER_UPDATE_PROFILE: '/seller/profile/update.php',
  
  // Buyer Properties
  PROPERTIES: '/buyer/properties/list.php',
  PROPERTY_DETAILS: '/buyer/properties/details.php',
  
  // Buyer Inquiries
  SEND_INQUIRY: '/buyer/inquiries/send.php',
  
  // Buyer Profile
  BUYER_PROFILE: '/buyer/profile/get.php',
  BUYER_UPDATE_PROFILE: '/buyer/profile/update.php',
  
  // Favorites
  TOGGLE_FAVORITE: '/buyer/favorites/toggle.php',
  FAVORITES_LIST: '/buyer/favorites/list.php',
  
  // History
  HISTORY_ADD: '/buyer/history/add.php',
  HISTORY_LIST: '/buyer/history/list.php',
  
  // OTP
  SEND_EMAIL_OTP: '/otp/send-email.php',
  VERIFY_EMAIL_OTP: '/otp/verify-email.php',
  SEND_SMS_OTP: '/otp/send-sms.php',
  VERIFY_SMS_OTP: '/otp/verify-sms.php',
  RESEND_SMS_OTP: '/otp/resend-sms.php',
  OTP_MSG91_SEND: '/otp/msg91-send.php',
  OTP_MSG91_VERIFY: '/otp/msg91-verify.php',
  
  // Upload
  UPLOAD_PROFILE_IMAGE: '/upload/profile-image.php',
  UPLOAD_PROPERTY_FILES: '/upload/property-files.php',
  
  // Image Moderation
  MODERATE_AND_UPLOAD: '/images/moderate-and-upload.php',
  
  // Admin
  ADMIN_LOGIN: '/admin/auth/login.php',
  ADMIN_VERIFY: '/admin/auth/verify.php',
  ADMIN_SEND_OTP: '/admin/auth/send-otp.php',
  ADMIN_VERIFY_OTP: '/admin/auth/verify-otp.php',
  ADMIN_RESEND_OTP: '/admin/auth/resend-otp.php',
  ADMIN_DASHBOARD_STATS: '/admin/dashboard/stats.php',
  ADMIN_USERS_LIST: '/admin/users/list.php',
  ADMIN_USERS_UPDATE: '/admin/users/update.php',
  ADMIN_USERS_DELETE: '/admin/users/delete.php',
  ADMIN_PROPERTIES_LIST: '/admin/properties/list.php',
  ADMIN_PROPERTIES_APPROVE: '/admin/properties/approve.php',
  ADMIN_PROPERTIES_REJECT: '/admin/properties/reject.php',
  ADMIN_PROPERTIES_DELETE: '/admin/properties/delete.php',
  ADMIN_AGENTS_LIST: '/admin/agents/list.php',
  ADMIN_AGENTS_VERIFY: '/admin/agents/verify.php',
  ADMIN_AGENTS_UNVERIFY: '/admin/agents/unverify.php',
  ADMIN_SUBSCRIPTIONS_LIST: '/admin/subscriptions/list.php',
  ADMIN_SUPPORT_LIST: '/admin/support/list.php',
  ADMIN_SUPPORT_UPDATE_STATUS: '/admin/support/update-status.php',
  ADMIN_CHANGE_PASSWORD: '/admin/auth/change-password.php',
  
  // Chat
  CHAT_CREATE_ROOM: '/chat/create-room.php',
  CHAT_LIST_ROOMS: '/chat/list-rooms.php',
  
  // Buyer Interactions (Rate Limiting)
  BUYER_INTERACTIONS_CHECK: '/buyer/interactions/check.php',
  BUYER_INTERACTIONS_RECORD: '/buyer/interactions/record.php',
  
  // Public Stats
  PUBLIC_STATS: '/public/stats.php',
  PUBLIC_CONFIG: '/public/config.php',

  // Contact form (sendmail to sneha@vedantinfoedge.com)
  CONTACT_SENDMAIL: '/home/sendmail.php',

  // Razorpay Payment
  PAYMENT_CREATE_ORDER: '/payment/create-order.php',
  PAYMENT_VERIFY: '/payment/verify.php',
};

export default API_BASE_URL;
